import { CircularProgress, TextField, useFormControl, useTheme } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import React from 'react';
import {useGetAccountsByNameAndLimitQuery} from "../../api/admin.api.ts";
import {ACCOUNT_LABEL} from "../../constants/messages.tsx";

interface AccountTypeProps {
    id?: number;
    name: string;
    value: { label: string; value: string };
    onChange: (onChange: React.ChangeEvent<HTMLInputElement>, value: { label: string; value: string }) => void;
    onClose?: (onClose: React.ChangeEvent<HTMLInputElement>, value: { label: string; value: string }) => void;
		error?: boolean;
}

function AccountType({ name, value, id, onChange, onClose, error }: AccountTypeProps) {
    const inputName = id !== undefined ? `${name}-${id}` : name;
    const [searchValue, setSearchValue] = React.useState('');
    const [inputValue, setInputValue] = React.useState('');
		const [focused, setFocused] = React.useState(false);


		const formControl = useFormControl();
		const theme = useTheme();

    const { data: accountTypesFromApi, isFetching } = useGetAccountsByNameAndLimitQuery({
        searchValue,
        limit: 9
    });

    // Track previous value prop to only sync inputValue when value changes
    const prevValueRef = React.useRef<string | undefined>(String(value.value));

    React.useEffect(() => {
        if (value.value !== prevValueRef.current) {
            const selected = accountTypesFromApi?.find(option => option.value === value.value);
            if (selected) {
                setInputValue(selected.label);
            }
            prevValueRef.current = String(value.value);
        }
    }, [value, accountTypesFromApi]);

    // Debounce searchValue update from inputValue
    React.useEffect(() => {
        const handler = setTimeout(() => {
            setSearchValue(inputValue);
        }, 500);
        return () => {
            clearTimeout(handler);
        };
    }, [inputValue]);

    // Store API options separately
    const mergedOptions = React.useMemo(() => {
        const options = accountTypesFromApi || [];
        // Include the current value if it's not already in the options
        return value.value && !options.some(option => option.value === value.value)
            ? [value, ...options]
            : options;
    }, [value, accountTypesFromApi]);

		// Memoize sx for performance and theme consistency
		const sx = React.useMemo(() => ({
			minWidth: '460px',
			'& .MuiOutlinedInput-root': {
				'& fieldset': {
					borderColor: (formControl?.error || error)
						? theme.palette.error.main
						: theme.palette.grey[400],
					borderWidth: (formControl?.error || error) && focused ? 2 : 1,
				},
			},
		}), [formControl?.error, error, focused, theme]);

    return (
        <Autocomplete
            sx={sx}
            disableClearable
            options={isFetching ? [] : mergedOptions || []}
            filterOptions={(x) => x}
            value={value}
            inputValue={inputValue}
            onInputChange={(_, newInputValue) => {
                setInputValue(newInputValue);
            }}
            isOptionEqualToValue={(option, val) => option.value === val?.value}
            getOptionLabel={option => option.label || ''}
            onChange={(_, newValue) => {
                if (onChange) {
                    onChange({
                        target: {
                            name: inputName,
                            value: newValue ? newValue.value : ''
                        },
                    } as React.ChangeEvent<HTMLInputElement>, newValue);
                }
            }}
            onClose={(e, reason) => {
                if (onClose && reason !== 'selectOption') {
                    const textContent = (e.target as HTMLElement).textContent;
                    onClose({
                        target: {
                            name: inputName,
                            value: textContent,
                        },
                    } as React.ChangeEvent<HTMLInputElement>, value);
                }
            }}
            loading={isFetching}
            loadingText={
                <span style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: 8 }}>Loading...</span>
                    <CircularProgress size={20} />
                </span>
            }
            noOptionsText="No Accounts Found"
            renderInput={(params) => (
                <TextField
										required
                    error={formControl?.error}
                    name={inputName}
                    {...params}
                    label={ACCOUNT_LABEL}
                    slotProps={{ inputLabel: { shrink: true } }}
                    variant="outlined"
                    sx={{
                        width: '100%',
                        minWidth: '240px',
                        maxWidth: '500px'
                    }}
										onFocus={() => setFocused(true)}
										onBlur={() => setFocused(false)}
                />
            )}
        />
    );
}

export default AccountType;
